import * as Sentry from "@sentry/react";
import { env } from "./env";

const DEFAULT_ASSET_PREFIX = "https://ai.pollen.au/dist/";

type FirstPartyAssetMatcher = {
  prefixes: string[];
  urlPatterns: RegExp[];
};

function escapeRegExp(value: string): string {
  if (!value)
    return "";

  return value.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
}

function getHttpAssetPrefixFromUrl(url: string): string | null {
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:")
      return null;
    return new URL(".", parsedUrl).toString();
  }
  catch {
    return null;
  }
}

function getImportMetaAssetPrefix(): string | null {
  return getHttpAssetPrefixFromUrl(import.meta.url);
}

function getCurrentScriptAssetPrefix(): string | null {
  if (typeof document === "undefined")
    return null;

  const currentScript = document.currentScript;
  if (!(currentScript instanceof HTMLScriptElement) || !currentScript.src)
    return null;

  return getHttpAssetPrefixFromUrl(currentScript.src);
}

function getRuntimeAssetPrefix(): string | null {
  return getImportMetaAssetPrefix() ?? getCurrentScriptAssetPrefix();
}

function buildFirstPartyAssetMatcher(defaultPrefix: string): FirstPartyAssetMatcher {
  const runtimePrefix = getRuntimeAssetPrefix();
  const prefixes = runtimePrefix && runtimePrefix !== defaultPrefix
    ? [defaultPrefix, runtimePrefix]
    : [defaultPrefix];

  return {
    prefixes,
    urlPatterns: prefixes.map(prefix => new RegExp(`^${escapeRegExp(prefix)}`)),
  };
}

function getEventFrameUrls(event: Sentry.Event): string[] {
  const frames = event.exception?.values?.flatMap(v => v.stacktrace?.frames ?? []) ?? [];

  return frames.flatMap((frame) => {
    if (typeof frame.filename !== "string")
      return [];
    return [frame.filename];
  });
}

function isFirstPartyFrameUrl(url: string, firstPartyMatcher: FirstPartyAssetMatcher): boolean {
  if (firstPartyMatcher.prefixes.some(prefix => url.startsWith(prefix)))
    return true;

  return firstPartyMatcher.urlPatterns.some(pattern => pattern.test(url));
}

function touchesOurCode(event: Sentry.Event, firstPartyMatcher: FirstPartyAssetMatcher): boolean {
  const frameUrls = getEventFrameUrls(event);
  if (frameUrls.length === 0)
    return false;

  return frameUrls.some(url => isFirstPartyFrameUrl(url, firstPartyMatcher));
}

function getHostTagValue(): string {
  if (typeof window === "undefined")
    return "unknown";

  const { protocol, hostname, host } = window.location;
  if (hostname)
    return hostname;
  if (protocol === "file:")
    return "local-file";
  if (host)
    return host;
  return "unknown";
}

function normalizeTracesSampleRate(sampleRate: number): number {
  if (!Number.isFinite(sampleRate))
    return 0;

  if (sampleRate < 0)
    return 0;

  if (sampleRate > 1)
    return 1;

  return sampleRate;
}

if (env.SENTRY_DSN && env.PROD) {
  const firstPartyMatcher = buildFirstPartyAssetMatcher(DEFAULT_ASSET_PREFIX);

  try {
    Sentry.init({
      enabled: env.SENTRY_ENABLED,
      dsn: env.SENTRY_DSN,
      environment: env.SENTRY_ENVIRONMENT,

      // Keep tracing low and env-configurable for embed performance triage.
      tracesSampleRate: normalizeTracesSampleRate(env.SENTRY_TRACES_SAMPLE_RATE),

      // Embed mode: explicit/manual capture only.
      defaultIntegrations: false,
      integrations: [Sentry.inboundFiltersIntegration()],

      allowUrls: firstPartyMatcher.urlPatterns,
      denyUrls: [
        /extensions\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
      ],

      beforeSend(event) {
        // Hard gate: if it is not our stack, it is not ours
        if (!touchesOurCode(event, firstPartyMatcher))
          return null;

        const host = getHostTagValue();
        event.tags = {
          ...event.tags,
          component: "pollen-drawer-ai-widget",
          host,
          asset: "embed.drawer-widget.umd.js",
        };

        // Privacy: strip headers; keep only host for context
        if (event.request) {
          delete event.request.headers;
          if (event.request.url)
            event.request.url = host;
        }

        return event;
      },

      // API URL is per-element at runtime; only propagate traces to the
      // optional build-time fallback if one is configured.
      tracePropagationTargets: env.API_URL
        ? [new RegExp(escapeRegExp(env.API_URL))]
        : [],
    });
  }
  catch (error) {
    console.warn("[Sentry] Failed to initialize Pollen Drawer widget instrumentation", error);
  }
}

export { Sentry };
